import { stringify } from 'qs';
import _ from 'lodash';
import moment from 'moment';
import { dateWithSlash, dayFormat, timeFormatShort, yearMonthDayFormat } from '../constants/date';
import { jobStatuses, jobWorkerStatus, maxWorkingHoursValue, minJobHoursValue, workerActualMinutesValues } from '../constants/jobs';
import { errorMaxJobHoursMessage, errorMinJobHoursMessage, confirmationMessages } from '../constants/text';
import { getDateWithOffset, getNowInTimeZone, getTimezoneAbbr, isJobDateSoonOrPast, isTimeZoneMatchesCurrent } from './timezones';
import { renderDay } from './date';
import UsersService from '../services/UsersService';

function formatDateForRequest (momentObject) {
  return moment(momentObject).format(yearMonthDayFormat);
}

const allowedFilterParams = ['statuses', 'skills', 'get_not_completed', 'company_id', 'event',
  'start_date_from', 'start_date_to', 'invoice_date_from', 'invoice_date_to'];

/**
 * @param {object} filterParams
 * @return {string}
 * */
export function stringifyJobsFilterParams (filterParams) {
  let { period, filteringValues, ...filter } = filterParams;
  filter = {
    ...filter,
    ...filteringValues
  };
  const filterData = {};

  for (let key in filter) {
    if (filter.hasOwnProperty(key)) {
      if (!allowedFilterParams.includes(key)) {
        continue;
      }

      const filterParam = filter[key];

      if (filterParam instanceof moment) {
        filterData[key] = formatDateForRequest(filterParam);
      } else if (_.isArray(filterParam) && filterParam.length) {
        filterData[key] = filterParam.map(item => item.value || item);
      } else if (filterParam && key === 'event') {
        filterData.event_id = filterParam.value;
      } else if (filterParam === true || _.isNumber(filterParam) || _.isString(filterParam)) {
        filterData[key] = filterParam;
      }
    }
  }

  const queryString = !_.isEmpty(filterData) ? `&${stringify(filterData)}` : '';
  const urlString = `&${stringify({ period })}` + queryString;

  return ({ queryString, urlString, period });
}

/**
 * @param {object} filterParams
 * @return {string}
 * */
export function stringifyWorkerJobsFilterParams (filterParams) {
  const { period, worker_id, statuses } = filterParams;

  const queryData = { worker_id };
  const urlData = { period };

  if (statuses) {
    const statusesArray = [statuses];

    if (statuses === jobWorkerStatus.WORKER_CANCELLED) {
      statusesArray.push(jobWorkerStatus.WORKER_CANCELLED_CONSEQUENCE);
    }

    queryData.statuses = statusesArray;
    urlData.statuses = statuses;
  }

  const queryString = `&${stringify(queryData)}`;
  const urlString = `&${stringify(urlData)}`;

  return ({ queryString, urlString, period, worker_id });
}

export const isJobSaved = status => status === jobStatuses.saved.server;
export const isJobCompleted = status => status === jobStatuses.completed.server;
export const isJobCancelled = status => status === jobStatuses.cancelled.server;
export const isJobPosted = status => status === jobStatuses.posted.server;
export const isJobFilled = status => status === jobStatuses.filled.server;
export const isJobDeleted = status => status === jobStatuses.deleted.server;
export const isJobNotEditable = (status, is_approved) => {
  return is_approved || isJobCompleted(status) || isJobCancelled(status) || isJobDeleted(status);
};
export const isJobHasNotStarted = (start_date, time_zone_id) => start_date > getNowInTimeZone(time_zone_id);
export const isJobEnded = (end_date, time_zone_id) => end_date < getNowInTimeZone(time_zone_id);

// For past jobs we don't show status for posted job (filled/unfilled) if there are hired workers.
// In this case we show message that job is needed to be approved
export const needToHideStatusBadge = (isPastJob, status, hasHiredWorkers) =>
  isPastJob && (isJobPosted(status) || isJobFilled(status)) && hasHiredWorkers;

export const isWorkerOffered = status => status === jobWorkerStatus.OFFERED;
export const isWorkerHired = status => status === jobWorkerStatus.HIRED;

export function parseHoursMinutes (actual_hours) {
  let hours = 0;
  let minutes = 0;
  if (typeof actual_hours === 'string') {
    actual_hours = parseFloat(actual_hours);
  }
  if (actual_hours) {
    [hours, minutes] = actual_hours.toFixed(2).split('.');
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);
  }
  let minutesUI = '';
  const minutesData = workerActualMinutesValues.find(item => item.value === minutes);
  if (minutesData) {
    minutesUI = minutesData.label;
  }
  return {
    hours,
    minutes,
    minutesUI
  };
}

/**
 * @param {string|moment.Moment|Date} startTime
 * @param {string|moment.Moment|Date} endTime
 * @param {number} [actual_hours]
 * @return {string}
 * @example '6:45am - 7:00pm (12 hrs 15 min)' || '6:45am - 7:00pm'
 */
export function getTimeInfoUi (startTime, endTime, actual_hours) {
  const startTimeUi = moment(startTime).format(timeFormatShort);
  const endTimeUi = moment(endTime).format(timeFormatShort);
  const timeDiffUi = `${startTimeUi} - ${endTimeUi}`;
  if (actual_hours) {
    const duration = getJobTimeDurationUi(actual_hours);
    return `${timeDiffUi} (${duration})`;
  }
  return timeDiffUi;
}

/**
 * @param {number} actual_hours
 * @return {string}
 * @example '12 hrs 15 min' || '12 hrs'
 */
export function getJobTimeDurationUi (actual_hours) {
  const { minutes, minutesUI, hours } = parseHoursMinutes(actual_hours);
  return `${hours} hrs${minutes ? ` ${minutesUI} min` : ''}`;
}

/**
 * @param {moment.Moment} startDate
 * @param {moment.Moment} startTime
 * @param {moment.Moment} endTime
 * @return {object}
 */
export function resolveDateTime (startDate, startTime, endTime) {
  startTime = startDate.clone().set({
    hours: startTime.get('hours'),
    minutes: startTime.get('minutes')
  });

  endTime = startDate.clone().set({
    hours: endTime.get('hours'),
    minutes: endTime.get('minutes')
  });

  if (startTime.isSameOrAfter(endTime)) {
    endTime.add(1, 'days');
  }

  const jobDurationMinutes = endTime.diff(startTime, 'minutes');
  const hours = jobDurationMinutes / 60;
  let hoursErrorMessage = null;
  if (hours > maxWorkingHoursValue) {
    hoursErrorMessage = errorMaxJobHoursMessage;
  } else if (hours < minJobHoursValue) {
    hoursErrorMessage = errorMinJobHoursMessage;
  }
  return {
    startTime,
    endTime,
    hours,
    hoursErrorMessage
  };
}

export function getCancelJobConfirmationMessage (userRole, status, start_date, hiredWorkersQuantity = 0) {
  const shouldViewJobSoonMessage = hiredWorkersQuantity > 0 && (isJobPosted(status) || isJobFilled(status)) && !!start_date && isJobDateSoonOrPast(start_date);
  return shouldViewJobSoonMessage ? confirmationMessages.cancelJobWithin24Hours[userRole] : confirmationMessages.cancelJob;
}

/**
 *
 * @param {string|moment.Moment|Date} start_date
 * @param {string|moment.Moment|Date} end_date
 * @param {string} timezone='America/New_York' - {@see defaultTimeZone}
 * @param {string} dateFormat
 * @return {string} // example: Mon, Mar 4, 2019, 3:00pm - 2:00am (EST)
 */
export const getDateTimeShiftFullUi = (start_date, end_date, timezone, dateFormat) => {
  let dateTimeShiftUi = `${moment(start_date).format(dateFormat)}, ${getTimeInfoUi(start_date, end_date)}`;
  const timeZonesAreDifferent = !isTimeZoneMatchesCurrent(timezone);
  if (timeZonesAreDifferent) {
    dateTimeShiftUi += ` (${getTimezoneAbbr(start_date, timezone)})`;
  }
  return dateTimeShiftUi;
};

/**
 * @param {string} job_status
 * @return {boolean}
 */
export function shouldHoldStartTime (job_status) {
  return UsersService.isEmployer() && (isJobFilled(job_status) || isJobPosted(job_status));
}

/**
 * @param {string|moment.Moment|Date} start_date
 * @param {string|moment.Moment|Date} end_date
 * @param {number} break_time
 * @return {number}
 */
export function calculateActualHours (start_date, end_date, break_time = 0) {
  return Math.ceil(((moment(end_date).diff(moment(start_date), 'minutes') - break_time) / 60) / 0.25) * 0.25;
}

/**
 * @param {array} jobsList
 * @return {object}
 */
export function splitJobsByDate (jobsList) {
  const byDate = _.groupBy(jobsList, job => (
    getDateWithOffset(job.start_date, job.time_zone_id).startOf('day').format()
  ));

  return Object.keys(byDate).map(key => ({
    formattedDate: moment(key).format(dateWithSlash),
    weekDay: renderDay(moment(key), dayFormat),
    data: byDate[key]
  }));
}

/**
 * @param {array} workers
 * @return {array}
 */
export function getHiredWorkers (workers) {
  if (!_.isArray(workers)) {
    return [];
  }

  return workers.filter(worker => worker.status === jobWorkerStatus.HIRED);
}

/**
 * @param {array} workers
 * @return {number}
 */
export function getHiredWorkersQuantity (workers) {
  if (!workers) return 0;
  return getHiredWorkers(workers).length;
}

/**
 * @param {array} workers
 * @return {array}
 */
export function getJobWorkersToRate (workers) {
  return getHiredWorkers(workers).filter(job_worker => !job_worker.job_satisfaction_rating);
}

/**
 * @param {array} workers
 * @return {boolean}
 */
export function shouldRateWorkers (workers) {
  return UsersService.isEmployer() && (getJobWorkersToRate(workers).length > 0);
}



// WEBPACK FOOTER //
// ./src/helpers/jobs.js